import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/apiConfig';
import '../styles/PurchaseInvoicePreview.css';

const PurchaseReturnPreview = ({ data: initialData, onBack, onEdit }) => {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFullData = async () => {
            const prId = initialData?._id || initialData?.id;
            
            if (prId) {
                setLoading(true);
                try {
                    const token = sessionStorage.getItem('token');
                    const endpoint = 'purchase-return';
                    const res = await axios.get(`${API_BASE_URL}/${endpoint}/${prId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data) {
                        setData(res.data);
                    }
                } catch (err) {
                    console.error("Error fetching full PO/Challan data:", err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchFullData();
    }, [initialData]);

    // Helper to convert number to words (Indian System)
    const numberToWords = (num) => {
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const inWords = (n) => {
            if (n < 20) return a[n];
            let s = b[Math.floor(n / 10)];
            if (n % 10 > 0) s += ' ' + a[n % 10].trim();
            return s;
        };

        const convert = (n) => {
            if (n === 0) return '';
            let res = '';
            if (n >= 10000000) {
                res += convert(Math.floor(n / 10000000)) + 'Crore ';
                n %= 10000000;
            }
            if (n >= 100000) {
                res += inWords(Math.floor(n / 100000)) + ' Lakh ';
                n %= 100000;
            }
            if (n >= 1000) {
                res += inWords(Math.floor(n / 1000)) + ' Thousand ';
                n %= 1000;
            }
            if (n >= 100) {
                res += inWords(Math.floor(n / 100)) + ' Hundred ';
                n %= 100;
            }
            if (n > 0) {
                if (res !== '') res += 'and ';
                res += inWords(Math.floor(n));
            }
            return res;
        };

        if (num === 0) return 'Zero Only';
        const parts = num.toFixed(2).split('.');
        let result = convert(parseInt(parts[0]));
        if (parts.length > 1 && parseInt(parts[1]) > 0) {
            result += ' and ' + inWords(parseInt(parts[1])) + ' Paise';
        }
        return result.trim() + ' Only';
    };

    // Correctly calculate Item Sum from items array instead of using grand total
    const productItemsArr = (() => {
        const itemsRaw = data?.items;
        let itemsArray = [];
        try {
            itemsArray = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : (itemsRaw || []);
        } catch (e) { itemsArray = []; }
        return Array.isArray(itemsArray) ? itemsArray : [];
    })();

    const calculatedItemSum = productItemsArr.reduce((sum, item) => sum + (parseFloat(item.total) || parseFloat(item.amount) || 0), 0);
    const itemSum = calculatedItemSum;
    
    const discountPercentage = parseFloat(data?.discount || 0);
    const discountAmount = data?.discount_type === '%' ? (itemSum * discountPercentage) / 100 : discountPercentage;

    const freightPercentage = parseFloat(data?.freight || 0);
    const freightAmount = (itemSum * freightPercentage) / 100;
    const insurancePercentage = parseFloat(data?.insurance || 0);
    const insuranceAmount = (itemSum * insurancePercentage) / 100;
    const extraCharges = parseFloat(data?.extra_charges_amount || data?.extraChargesAmount || 0);

    // New Subtotal = Item Sum - Discount + Extra Charges + Freight + Insurance
    const subTotal = itemSum - discountAmount + extraCharges + freightAmount + insuranceAmount;
    
    // Detect Local vs Interstate based on Sale Type name (highest priority for user logic)
    const saleTypeName = (data?.saleType || data?.sale_type_name || '').toLowerCase();
    const isInterstateManual = saleTypeName.includes('interstate');
    const isLocalManual = saleTypeName.includes('local');

    // Use saved percentages but override based on "Interstate" keyword if necessary
    const rawTax1 = data?.tax1 !== undefined && data?.tax1 !== null ? data.tax1 : (data?.sale_type_tax1 || 0);
    const rawTax2 = data?.tax2 !== undefined && data?.tax2 !== null ? data.tax2 : (data?.sale_type_tax2 || 0);
    
    let taxPercentage1 = parseFloat(rawTax1);
    let taxPercentage2 = parseFloat(rawTax2);

    // STRICT USER LOGIC: If Interstate is selected, STATE/UT TAX must be 0
    if (isInterstateManual) {
        taxPercentage2 = 0;
    }

    const companyStateStr = (data?.companyState || data?.company_state || data?.Seller?.seller_state || data?.seller_state || '').toLowerCase();
    const isLocal = !isInterstateManual && (isLocalManual || companyStateStr.includes('gujarat') || companyStateStr === '');
    
    const dbCgst = data?.central_tax !== undefined && data?.central_tax !== null ? parseFloat(data.central_tax) : null;
    const dbSgst = data?.state_tax !== undefined && data?.state_tax !== null ? parseFloat(data.state_tax) : null;

    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;

    if (isLocal) {
        cgstAmount = dbCgst !== null ? dbCgst : (subTotal * taxPercentage1) / 100;
        sgstAmount = dbSgst !== null ? dbSgst : (subTotal * taxPercentage2) / 100;
    } else {
        igstAmount = (dbCgst !== null || dbSgst !== null) 
            ? ((dbCgst || 0) + (dbSgst || 0)) 
            : (subTotal * (taxPercentage1 + taxPercentage2)) / 100;
    }
    const totalTax = cgstAmount + sgstAmount + igstAmount;

    // Final Grand Total
    const grandTotal = parseFloat(data?.total_amount) || (subTotal + totalTax);

    // Build invoice object from dynamic data prop
    const invoice = {
        companyName: data?.Seller?.seller_name || data?.seller_name || 'No Name Found',
        address: data?.Seller?.seller_address || data?.seller_address || '',
        city: data?.Seller?.seller_city ? `${data.Seller.seller_city} ${data?.Seller?.seller_state || ''}` : (data?.seller_state || ''),
        mobile: `Mo.: ${data?.Seller?.seller_contact || ''}`,
        email: `E-mail: ${data?.Seller?.seller_email || ''}`,
        invoiceType: data?.invoice_type_name || 'PURCHASE RETURN',

        // Company (Seller) details
        pan: data?.Seller?.seller_pan_card || '',
        gstin: data?.Seller?.seller_gst || data?.seller_gst || '',
        cin: data?.Seller?.seller_cin || '',

        // Invoice details
        invoiceNo: data?.return_no || '',
        invoiceDate: data?.return_date ? new Date(data.return_date).toLocaleDateString('en-GB') : '',

        // Buyer details (Own Company)
        buyer: {
            name: data?.Company?.company_name || '',
            address: data?.Company?.company_address || '',
            city: [
                data?.Company?.company_city,
                data?.Company?.company_state,
                data?.Company?.company_pin_code
            ].filter(Boolean).join(' '),
            pan: data?.Company?.company_pan_card_number || '',
            gstin: data?.Company?.company_gst_number || '',
            cin: data?.Company?.company_cin_number || '',
            placeOfSupply: data?.Company?.company_state || ''
        },

        // Consignee details (Same as buyer)
        consignee: {
            name: data?.Company?.company_name || '',
            address: data?.Company?.company_address || '',
            city: [
                data?.Company?.company_city,
                data?.Company?.company_state,
                data?.Company?.company_pin_code
            ].filter(Boolean).join(' '),
            pan: data?.Company?.company_pan_card_number || '',
            gstin: data?.Company?.company_gst_number || '',
            cin: data?.Company?.company_cin_number || '',
            placeOfSupply: data?.Company?.company_state || ''
        },

        // Transport details
        vehicleNo: data?.vehicleNo || data?.vehicle_no || '',
        lrNo: data?.lrNo || data?.lr_no || '',
        eWayBillNo: data?.eWayBillNo || data?.e_way_bill_no || '',

        // Items
        items: (() => {
            const itemsRaw = data?.items;
            let itemsArray = [];
            try {
                itemsArray = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : (itemsRaw || []);
            } catch (e) {
                console.error("Error parsing items:", e);
            }
            return Array.isArray(itemsArray) ? itemsArray : [];
        })().map((item, idx) => ({
            srNo: idx + 1,
            hsnCode: item.hsnCode || item.hsn_code || '',
            category: item.category || '',
            product: item.product || '',
            color: item.color || '',
            grade: item.grade || '',
            qty: item.quantity || item.qty || 0,
            unit: item.unit || 'PCS',
            rate: item.rate || 0,
            amount: item.total || item.amount || 0
        })),

        // Totals
        totals: {
            totalQty: (() => {
                const itemsRaw = data?.items;
                let itemsArray = [];
                try {
                    itemsArray = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : (itemsRaw || []);
                } catch (e) {
                    itemsArray = [];
                }
                const arr = Array.isArray(itemsArray) ? itemsArray : [];
                return arr.reduce((sum, item) => sum + (Number(item.quantity) || Number(item.qty) || 0), 0);
            })(),
            discountAmount: discountAmount.toFixed(2),
            itemSum: itemSum.toFixed(2),
            freightPercent: freightPercentage,
            freight: freightAmount.toFixed(2),
            insurancePercent: insurancePercentage,
            insurance: insuranceAmount.toFixed(2),
            subTotal: subTotal.toFixed(2),
            isLocal,
            tax1Percent: taxPercentage1,
            tax2Percent: taxPercentage2,
            interstateTaxPercent: (taxPercentage1 + taxPercentage2),
            cgst: cgstAmount.toFixed(2),
            sgst: sgstAmount.toFixed(2),
            igst: igstAmount.toFixed(2),
            extraCharges: extraCharges.toFixed(2),
            totalTax: totalTax.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
            amountInWords: numberToWords(grandTotal)
        },

        // Bank details
        bankName: data?.Seller?.bank_name || '',
        accountName: data?.Seller?.bank_account_name || '',
        accountNumber: data?.Seller?.bank_account_number || '',
        ifscCode: data?.Seller?.bank_ifsc_code || '',

        // Footer
        remarks: data?.remarks || '',
        termsAndConditions: data?.termsCondtion || data?.terms_condtion || ''
    };

    // Handle Print functionality
    const handlePrint = () => {
        window.print();
    };

    // Handle PDF Download using jsPDF
    const handleDownloadPDF = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;
        const element = document.getElementById('invoice-content');
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save(`Invoice_${invoice.invoiceNo.replace(/\//g, '-')}.pdf`);
    };


    if (loading) return <div className="purchase-invoice-preview">Loading...</div>;

    return (
        <div className="purchase-invoice-preview">
            <div className="preview-container">
                <div className="invoice-wrapper" id="invoice-content">
                    <div className="invoice-box">
                        <div className="invoice-header">
                            <h1 className="company-name">{invoice.companyName}</h1>
                            <p className="company-address">{invoice.address}</p>
                            <p className="company-address">{invoice.city}</p>
                            <p className="company-contact">{invoice.mobile} {invoice.email}</p>
                            <h2 className="invoice-type">{invoice.invoiceType}</h2>
                        </div>

                        <div className="details-row">
                            <div className="details-left">
                                <div className="detail-item">PAN NO : {invoice.pan}</div>
                                <div className="detail-item">GSTIN NO : {invoice.gstin}</div>
                                <div className="detail-item">CIN/LLP NO : {invoice.cin}</div>
                            </div>
                            <div className="details-right">
                                <div className="detail-item">ORIGINAL FOR RECIPIENT [ ]</div>
                                <div className="detail-item">DUPALICATE FOR TRANSPORTER [ ]</div>
                                <div className="detail-item">TRIPALICATE FOR SUPPLIER [ ]</div>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-left">
                                <div className="section-title">DETAILS OF RECEIVER (BUYER DETAIL)</div>
                                <div className="info-content">
                                    <strong>{invoice.buyer.name}</strong><br />
                                    {invoice.buyer.address}<br />
                                    {invoice.buyer.city}<br />
                                    <strong>PAN NO:</strong> {invoice.buyer.pan}<br />
                                    <strong>GSTIN NO:</strong> {invoice.buyer.gstin}<br />
                                    <strong>PLACE OF SUPPLY:</strong> {invoice.buyer.placeOfSupply}
                                </div>
                            </div>
                            <div className="info-right">
                                <div className="invoice-detail"><strong>INVOICE NO.:</strong> {invoice.invoiceNo}</div>
                                <div className="invoice-detail"><strong>INVOICE DATE:</strong> {invoice.invoiceDate}</div>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-left">
                                <div className="section-title">DETAILS OF CONSIGNEE (SHIPPED DETAIL)</div>
                                <div className="info-content">
                                    <strong>{invoice.consignee.name}</strong><br />
                                    {invoice.consignee.address}<br />
                                    {invoice.consignee.city}<br />
                                    <strong>PAN NO:</strong> {invoice.consignee.pan}<br />
                                    <strong>GSTIN NO:</strong> {invoice.consignee.gstin}<br />
                                    <strong>PLACE OF SUPPLY:</strong> {invoice.consignee.placeOfSupply}
                                </div>
                            </div>
                            <div className="info-right">
                                <div className="invoice-detail"><strong>VEHICLE NO.:</strong> {invoice.vehicleNo}</div>
                                <div className="invoice-detail"><strong>L.R. NO:</strong> {invoice.lrNo}</div>
                                <div className="invoice-detail"><strong>E-WAY BILL NO:</strong> {invoice.eWayBillNo}</div>
                            </div>
                        </div>

                        <table className="invoice-table">
                            <thead>
                                <tr>
                                    <th>SR NO.</th>
                                    <th>CATEGORY</th>
                                    <th>PRODUCT NAME</th>
                                    <th>HSN CODE</th>
                                    <th>COLOR</th>
                                    <th>GRADE</th>
                                    <th>QTY</th>
                                    <th>UNIT</th>
                                    <th>RATE</th>
                                    <th>AMOUNT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.items.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.srNo}</td>
                                        <td>{item.category}</td>
                                        <td>{item.product}</td>
                                        <td>{item.hsnCode}</td>
                                        <td>{item.color}</td>
                                        <td>{item.grade}</td>
                                        <td>{item.qty}</td>
                                        <td>{item.unit}</td>
                                        <td className="text-right">{item.rate}</td>
                                        <td className="text-right">{item.amount}</td>
                                    </tr>
                                ))}
                                <tr className="total-row-preview">
                                    <td colSpan="6" className="text-center"><strong>TOTAL</strong></td>
                                    <td><strong>{invoice.totals.totalQty}</strong></td>
                                    <td></td>
                                    <td></td>
                                    <td className="text-right"><strong>{invoice.totals.itemSum}</strong></td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="invoice-footer">
                            <div className="footer-left">
                                <div className="footer-row"><span className="footer-label">REMARKS:</span><span className="footer-value">{invoice.remarks}</span></div>
                                <div className="footer-row"><span className="footer-label">RUPEES IN WORDS:</span><span className="footer-value"><strong>{invoice.totals.amountInWords}</strong></span></div>
                                <div className="footer-row"><span className="footer-label">BANK NAME:</span><span className="footer-value">{invoice.bankName} (A/C: {invoice.accountNumber}, IFSC: {invoice.ifscCode})</span></div>
                                <div className="footer-terms"><strong>TERMS & CONDITIONS:</strong><p>{invoice.termsAndConditions}</p></div>
                            </div>
                            <div className="footer-right">
                                <div className="amount-row"><span>FREIGHT</span><span className="text-right">{invoice.totals.freight}</span></div>
                                <div className="amount-row"><span>INSURANCE</span><span className="text-right">{invoice.totals.insurance}</span></div>
                                {parseFloat(invoice.totals.discountAmount) > 0 && (
                                    <div className="amount-row"><span>DISCOUNT</span><span className="text-right" style={{color: '#ef4444'}}>-{invoice.totals.discountAmount}</span></div>
                                )}
                                {parseFloat(invoice.totals.extraCharges) > 0 && (
                                    <div className="amount-row"><span>EXTRA CHARGES</span><span className="text-right">{invoice.totals.extraCharges}</span></div>
                                )}
                                <div className="amount-row"><strong>TAXABLE AMOUNT</strong><span className="text-right"><strong>{invoice.totals.subTotal}</strong></span></div>
                                {(parseFloat(invoice.totals.cgst) > 0 || (invoice.totals.tax1Percent > 0 && invoice.totals.isLocal)) && (
                                    <div className="amount-row"><span>CENTRAL TAX ({invoice.totals.tax1Percent}%)</span><span className="text-right">{invoice.totals.cgst}</span></div>
                                )}
                                {(parseFloat(invoice.totals.sgst) > 0 || (invoice.totals.tax2Percent === 0 && invoice.totals.tax1Percent > 0 && invoice.totals.isLocal)) && (
                                    <div className="amount-row"><span>STATE/UT TAX ({invoice.totals.tax2Percent}%)</span><span className="text-right">{invoice.totals.sgst}</span></div>
                                )}
                                {(parseFloat(invoice.totals.igst) > 0 || (!invoice.totals.isLocal && invoice.totals.interstateTaxPercent > 0)) && (
                                    <div className="amount-row"><span>INTERSTATE TAX ({invoice.totals.interstateTaxPercent || (parseFloat(invoice.totals.tax1Percent) + parseFloat(invoice.totals.tax2Percent))}%)</span><span className="text-right">{invoice.totals.igst}</span></div>
                                )}
                                <div className="amount-row grand-total"><strong>BILL AMOUNT</strong><span className="text-right"><strong>{invoice.totals.grandTotal}</strong></span></div>
                                <div className="signature-section"><p>FOR, {invoice.companyName}</p><p className="signature-line">[AUTHORISED SIGNATORY]</p></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="action-sidebar">
                    <button className="action-btn" onClick={onBack}>Back</button>
                    <button className="action-btn" onClick={onEdit}>Update</button>
                    <button className="action-btn" onClick={handleDownloadPDF}>PDF</button>
                    <button className="action-btn" onClick={handlePrint}>Print</button>
                </div>
            </div>
        </div>
    );
};

export default PurchaseReturnPreview;
